import React, { useState, useEffect, useRef } from 'react';
import { FaCog, FaSpinner, FaSave, FaCheck, FaUpload, FaUser, FaPhone, FaEnvelope, FaCertificate, FaBuilding, FaGlobe, FaImage, FaTimes, FaAward, FaIdCard, FaShieldAlt, FaFileAlt, FaCamera, FaMapMarkerAlt } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user, updateUserName } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    certifications: '',
    designation: '',
    city: '',
    company: '',
    website: '',
    company_logo: null,
    // New fields for advisor profile
    profile_photo: null,
    irda_licence: '',
    amfi_licence: '',
    ira_number: '',
    other_certification: ''
  });

  // Get title based on user role
  const getBrandingTitle = () => {
    switch (user?.role) {
      case 'super_admin':
        return 'Super Admin Branding Settings';
      case 'manager':
        return 'Manager Branding Settings';
      default:
        return 'Advisor Branding Settings';
    }
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/advisor/branding`);
      if (response.data) {
        setFormData({
          name: response.data.name || '',
          contact: response.data.contact || '',
          email: response.data.email || '',
          certifications: response.data.certifications || '',
          designation: response.data.designation || '',
          city: response.data.city || '',
          company: response.data.company || '',
          website: response.data.website || '',
          company_logo: response.data.company_logo || null,
          // New fields
          profile_photo: response.data.profile_photo || null,
          irda_licence: response.data.irda_licence || '',
          amfi_licence: response.data.amfi_licence || '',
          ira_number: response.data.ira_number || '',
          other_certification: response.data.other_certification || ''
        });
        if (response.data.company_logo) {
          setLogoPreview(response.data.company_logo);
        }
        if (response.data.profile_photo) {
          setPhotoPreview(response.data.profile_photo);
        }
      }
    } catch (error) {
      if (error.response?.status !== 404) {
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setFormData(prev => ({ ...prev, company_logo: base64 }));
        setLogoPreview(base64);
        setSaved(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearLogo = () => {
    setFormData(prev => ({ ...prev, company_logo: null }));
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setSaved(false);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setFormData(prev => ({ ...prev, profile_photo: base64 }));
        setPhotoPreview(base64);
        setSaved(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearPhoto = () => {
    setFormData(prev => ({ ...prev, profile_photo: null }));
    setPhotoPreview(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
    setSaved(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/advisor/branding`, {
        ...formData
      });
      
      // Update the user name in the auth context so it reflects immediately
      if (formData.name) {
        updateUserName(formData.name);
      }
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <FaSpinner className="animate-spin text-[#FFD700]" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h2 className="text-2xl font-bold text-[#FFD700] flex items-center space-x-3">
          <FaCog />
          <span>{getBrandingTitle()}</span>
        </h2>
        <p className="text-gray-400 mt-1">Configure your report branding - these details will appear on all PDF reports</p>
      </div>

      {/* Personal Details Section */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-6">Personal Details</h3>
        
        {/* Profile Photo Upload */}
        <div className="mb-6">
          <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
            <FaCamera className="text-[#FFD700]" />
            <span>Profile Photo</span>
          </label>
          
          <div className="flex items-center space-x-6">
            {/* Photo Preview */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full border-2 border-dashed border-[#2D5A8F] bg-[#0A1929] flex items-center justify-center overflow-hidden">
                {photoPreview ? (
                  <img 
                    src={photoPreview} 
                    alt="Profile Preview" 
                    className="w-full h-full object-cover"
                    data-testid="profile-photo-preview"
                  />
                ) : (
                  <FaUser size={32} className="text-gray-500" />
                )}
              </div>
              {photoPreview && (
                <button
                  onClick={clearPhoto}
                  className="absolute -top-1 -right-1 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all"
                  title="Remove photo"
                  data-testid="remove-photo-btn"
                >
                  <FaTimes size={10} />
                </button>
              )}
            </div>
            
            {/* Upload Button */}
            <div>
              <input
                type="file"
                ref={photoInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                className="hidden"
                data-testid="profile-photo-input"
              />
              <button
                onClick={() => photoInputRef.current?.click()}
                className="px-4 py-2 bg-[#0A1929] text-[#FFD700] rounded-lg hover:bg-[#1E3A5F] transition-all flex items-center space-x-2 border border-[#2D5A8F]"
                data-testid="upload-photo-btn"
              >
                <FaUpload size={14} />
                <span>Upload Photo</span>
              </button>
              <p className="text-gray-500 text-xs mt-2">Max: 5MB. Recommended: Square image</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
              <FaUser className="text-[#FFD700]" />
              <span>Name</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="Your Name"
              data-testid="branding-name"
            />
          </div>

          {/* Contact */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
              <FaPhone className="text-[#FFD700]" />
              <span>Contact</span>
            </label>
            <input
              type="tel"
              name="contact"
              value={formData.contact}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="+91 98765 43210"
              data-testid="branding-contact"
            />
          </div>

          {/* Certifications */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
              <FaCertificate className="text-[#FFD700]" />
              <span>Certifications</span>
            </label>
            <input
              type="text"
              name="certifications"
              value={formData.certifications}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="CFP, CWM"
              data-testid="branding-certifications"
            />
          </div>

          {/* Designation */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
              <FaAward className="text-[#FFD700]" />
              <span>Designation</span>
            </label>
            <input
              type="text"
              name="designation"
              value={formData.designation}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="Senior Financial Advisor"
              data-testid="branding-designation"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
              <FaEnvelope className="text-[#FFD700]" />
              <span>Email</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="advisor@example.com"
              data-testid="branding-email"
            />
          </div>

          {/* City */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
              <FaMapMarkerAlt className="text-[#FFD700]" />
              <span>City</span>
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="Mumbai, Delhi, etc."
              data-testid="branding-city"
            />
          </div>
        </div>
      </div>

      {/* Certification Details Section - Advisor/Manager Only */}
      {(user?.role === 'advisor' || user?.role === 'manager') && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-6 flex items-center space-x-2">
            <FaIdCard />
            <span>Certification & Licence Details</span>
          </h3>
          <p className="text-gray-400 text-sm mb-6">Enter your regulatory licence and certification numbers</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* IRDA Licence */}
            <div>
              <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
                <FaShieldAlt className="text-[#FFD700]" />
                <span>IRDA Licence Number</span>
              </label>
              <input
                type="text"
                name="irda_licence"
                value={formData.irda_licence}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                placeholder="Enter IRDA Licence Number"
                data-testid="irda-licence-input"
              />
            </div>

            {/* AMFI Licence */}
            <div>
              <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
                <FaIdCard className="text-[#FFD700]" />
                <span>AMFI Registration Number</span>
              </label>
              <input
                type="text"
                name="amfi_licence"
                value={formData.amfi_licence}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                placeholder="Enter AMFI ARN Number"
                data-testid="amfi-licence-input"
              />
            </div>

            {/* IRA Number */}
            <div>
              <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
                <FaFileAlt className="text-[#FFD700]" />
                <span>IRA Number</span>
              </label>
              <input
                type="text"
                name="ira_number"
                value={formData.ira_number}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                placeholder="Enter IRA Number"
                data-testid="ira-number-input"
              />
            </div>

            {/* Other Certification */}
            <div>
              <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
                <FaCertificate className="text-[#FFD700]" />
                <span>Other Certification</span>
              </label>
              <input
                type="text"
                name="other_certification"
                value={formData.other_certification}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                placeholder="Enter any other certification details"
                data-testid="other-certification-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Company Details Section */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-xl font-bold text-[#FFD700] mb-6">Company Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Company */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
              <FaBuilding className="text-[#FFD700]" />
              <span>Company</span>
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="Wealth Management Co."
              data-testid="branding-company"
            />
          </div>

          {/* Website */}
          <div>
            <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
              <FaGlobe className="text-[#FFD700]" />
              <span>Website</span>
            </label>
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="www.mywealth.com"
              data-testid="branding-website"
            />
          </div>
        </div>

        {/* Company Logo */}
        <div className="mt-6">
          <label className="block text-gray-400 text-sm mb-2 font-medium flex items-center space-x-2">
            <FaImage className="text-[#FFD700]" />
            <span>Company Logo</span>
          </label>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Upload Section */}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                className="hidden"
                data-testid="logo-upload-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-6 py-3 bg-[#0A1929] text-[#FFD700] rounded-lg hover:bg-[#1E3A5F] transition-all flex items-center space-x-2 border border-[#2D5A8F]"
              >
                <FaUpload />
                <span>Choose File</span>
              </button>
              <p className="text-gray-500 text-xs mt-2">
                {formData.company_logo ? 'Logo selected' : 'No file chosen'}
              </p>
              <p className="text-gray-500 text-xs mt-1">Max file size: 5MB. Recommended: 200x200px</p>
            </div>

            {/* Preview Section */}
            <div className="border-2 border-dashed border-[#2D5A8F] rounded-lg p-4 bg-[#0A1929] min-h-[150px] flex items-center justify-center relative">
              {logoPreview ? (
                <>
                  <img 
                    src={logoPreview} 
                    alt="Logo Preview" 
                    className="max-h-[130px] max-w-full object-contain"
                    data-testid="logo-preview"
                  />
                  <button
                    onClick={clearLogo}
                    className="absolute top-2 right-2 p-2 bg-[#F87171] text-white rounded-full hover:bg-red-600 transition-all"
                    title="Remove logo"
                  >
                    <FaTimes size={12} />
                  </button>
                </>
              ) : (
                <div className="text-center text-gray-500">
                  <FaImage size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Logo Preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={saveSettings}
        disabled={saving}
        className={`w-full py-4 font-bold rounded-lg transition-all flex items-center justify-center space-x-2 ${
          saved 
            ? 'bg-[#34D399] text-white' 
            : 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] hover:from-[#FFD700] hover:to-[#D4AF37]'
        } disabled:opacity-50`}
        data-testid="save-branding-btn"
      >
        {saving ? (
          <>
            <FaSpinner className="animate-spin" />
            <span>Saving...</span>
          </>
        ) : saved ? (
          <>
            <FaCheck />
            <span>Settings Saved Successfully!</span>
          </>
        ) : (
          <>
            <FaSave />
            <span>Save Branding Settings</span>
          </>
        )}
      </button>

      {/* Info Note */}
      <div className="bg-[#0A1929] rounded-lg p-4 text-gray-500 text-sm border border-[#2D5A8F]">
        <p className="font-medium mb-1 text-gray-400">Note:</p>
        <p>These branding details will be automatically included in all PDF reports generated for your clients. Ensure all information is accurate and professional.</p>
      </div>
    </div>
  );
};

export default Settings;
